import { db } from "../firebase";
import { doc, getDoc } from "firebase/firestore";

/**
 * Fetch all trips from Firestore
 * @returns {Promise<Array>} An array of trip objects with their IDs
 */
export const fetchAllTrips = async () => {
  // Reference to the all_packages document
  const packagesRef = doc(db, "packages", "all_packages");
  
  try {
    // Use getDoc with source option, which will try cache first then server
    const docSnap = await getDoc(packagesRef);
    
    if (!docSnap.exists()) {
      // console.log("No trips found!");
      return [];
    }
    
    // Convert the document data to an array of trips with IDs
    const tripsData = docSnap.data();
    const tripsArray = Object.keys(tripsData).map(tripId => ({
      id: tripId,
      ...tripsData[tripId]
    }));
    
    return tripsArray;
  } catch (error) {
    // console.error("Error fetching trips: ", error);
    // Return empty array instead of throwing so UI can still render with no data
    return [];
  }
};

/**
 * Fetch a specific trip by ID
 * @param {string} tripId - The ID of the trip to fetch
 * @returns {Promise<Object|null>} The trip object or null if not found
 */
export const fetchTripById = async (tripId) => {
  // Reference to the all_packages document
  const packagesRef = doc(db, "packages", "all_packages");
  
  try {
    // Use getDoc which attempts to get from cache first if offline
    const docSnap = await getDoc(packagesRef);
    
    if (!docSnap.exists()) {
      // console.log("No trips found!");
      return null;
    }
    
    // Get the specific trip data
    const tripsData = docSnap.data();
    
    if (!tripsData[tripId]) {
      // console.log(`Trip with ID ${tripId} not found!`);
      return null;
    }
    
    // Return the trip with its ID
    return {
      id: tripId,
      ...tripsData[tripId]
    };
  } catch (error) {
    // console.error("Error fetching trip: ", error);
    return null;
  }
};

/**
 * Fetch trips by category
 * @param {string} category - The category to filter by
 * @returns {Promise<Array>} An array of trip objects matching the category
 */
export const fetchTripsByCategory = async (category) => {
  try {
    const allTrips = await fetchAllTrips();
    const filtered = allTrips.filter(trip => {
      if (!trip.category) return false;
      // Split by comma, trim, and check for exact match
      return trip.category
        .split(',')
        .map(s => s.trim().toLowerCase())
        .includes(category.toLowerCase());
    });
    return filtered;
  } catch (error) {
    // console.error("Error fetching trips by category: ", error);
    throw error;
  }
};

/**
 * Search trips by keyword (in name, destination, or category)
 * @param {string} keyword - The search term
 * @returns {Promise<Array>} An array of matching trip objects
 */
export const searchTrips = async (keyword) => {
  try {
    const allTrips = await fetchAllTrips();
    const searchTerm = keyword.toLowerCase();
    
    return allTrips.filter(trip => 
      trip.tripname.toLowerCase().includes(searchTerm) ||
      trip.location.toLowerCase().includes(searchTerm) ||
      trip.category.toLowerCase().includes(searchTerm)
    );
  } catch (error) {
    // console.error("Error searching trips: ", error);
    throw error;
  }
};

/**
 * Fetch trips by location
 * @param {string} location - The location to filter by
 * @returns {Promise<Array>} An array of trip objects matching the location
 */
export const fetchTripsByLocation = async (location) => {
  try {
    const allTrips = await fetchAllTrips();
    
    // Normalize the search location
    const normalizeLocation = (loc) => {
      return loc.toLowerCase()
        .replace(/\s+/g, '') // Remove all spaces
        .replace(/[^a-z0-9]/g, ''); // Remove special characters
    };
    
    const searchLocationNormalized = normalizeLocation(location);
    
    const filtered = allTrips.filter(trip => {
      if (!trip.location) return false;
      
      const tripLocationNormalized = normalizeLocation(trip.location);
      
      // Check if normalized locations match or if one contains the other
      return tripLocationNormalized.includes(searchLocationNormalized) || 
             searchLocationNormalized.includes(tripLocationNormalized);
    });
    
    return filtered;
  } catch (error) {
    // console.error("Error fetching trips by location: ", error);
    return [];
  }
};
